<template>
  <div style="min-width: 750px; min-height: 100%;">
    <div class="is-flex is-align-items-center is-justify-content-space-between mb-3">
      <div class="is-flex is-align-items-center">
        <a @click="toggle" class="button is-info">Toggle</a>
        <div class="horizontal_space"></div>
        <a
          v-if="show_pdf"
          @click="toggleOriginal"
          :class="{ 'is-loading': is_loading_original }"
          class="button is-info"
          >{{ showing_original ? 'קובץ מעובד' : 'קובץ מקור' }}</a
        >
      </div>
      <p v-if="pdfTitle">{{ pdfTitle }}</p>
      <p v-if="pdfDate">{{ $moment(pdfDate).format('DD/MM/YYYY hh:mm') }}</p>
    </div>
    <div v-if="is_loading" class="is-flex is-align-items-center is-justify-content-center">
      <div class="loading"></div>
      <div class="horizontal_space"></div>
      <p class="bold">טוען פרופיל מועמד/ת...</p>
    </div>
    <div v-else :class="containerStyle" style="height: 100%;">
      <iframe v-if="show_pdf" :src="pdf_src" width="100%" height="100%"></iframe>
      <applicant-profile-pdf v-else :payload="{ applicant_id: payload.resource_id }"></applicant-profile-pdf>
    </div>
  </div>
</template>
<script>
import ApplicantProfilePdf from '@/components/ApplicantProfilePdf'
export default {
  name: 'applicant-cv-pdf',
  props: ['payload', 'containerStyle'],
  data () {
    return {
      is_loading: true,
      is_loading_original: false,
      show_pdf: false,
      showing_original: false,
      pdf_src: undefined,
      pdfTitle: undefined,
      pdfDate: undefined,
      processed_view: undefined,
      original_view: undefined
    }
  },
  async created () {
    try {
      // Prefer the server-side pick: files_server chooses the newest CV whose
      // CONTENT is not the generated confidential header. A branded CV that
      // re-entered under the candidate's own name (stargate self-application)
      // is indistinguishable by filename, so pickCv below cannot catch it.
      let latest = null
      try {
        const original = await this.$network_utils.getOriginalCv({
          resource_id: this.payload.resource_id,
          resource_type: 'Applicant'
        })
        if (original && original.name) latest = original
      } catch (err) {
        // No original found (404) or endpoint unavailable — fall back below.
      }

      if (!latest) {
        const files = await this.$network_utils.getFiles({ resource_id: this.payload.resource_id })
        latest = this.pickCv(files)
      }

      this.is_loading = false

      if (!latest) {
        this.show_pdf = false
        return
      }

      this.show_pdf = true
      this.pdfTitle = latest.name
      this.pdfDate = latest.uploaded_at

      const blobData = await this.$network_utils.getFile({
        file_name: latest.name,
        resource_id: this.payload.resource_id,
        resource_type: 'Applicant'
      })

      const blob = new Blob([blobData], { type: 'application/pdf' })
      this.pdf_src = URL.createObjectURL(blob) + '#toolbar=0&navpanes=0&zoom=100'
      this.processed_view = { src: this.pdf_src, title: latest.name, date: latest.uploaded_at }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load CV:', err)
      this.is_loading = false
    }
  },
  methods: {
    // Picks the CV to display out of a file list (visible folder or archive).
    pickCv (files) {
      // System-generated confidential CVs (files_server super_lite variants) are
      // regenerated after every upload, so recency-sorting would always pick them
      // over the candidate's real CV. Never show them as "the" CV.
      const SYSTEM_CV_RE = /^cv_(suitup|piola|rebase|bullrun)\.pdf$/i
      files = (files || []).filter(({ name }) => !SYSTEM_CV_RE.test(name))

      const cvKeywords = [
        'קורות חיים', 'קורות החיים',
        'קורות_חיים', 'קורות_החיים',
        'קו"ח',
        'CV', 'cv', 'cV', 'Cv'

      ]
      const hrKeywords = ['HR', 'hr', 'hR', 'Hr']

      let matches = files.filter(({ name }) =>
        cvKeywords.some(kw => name.includes(kw)) &&
        !hrKeywords.some(hr => name.includes(hr))
      )

      if (!matches.length) {
        matches = files.filter(({ name }) =>
          cvKeywords.some(kw => name.includes(kw)) &&
          hrKeywords.some(hr => name.includes(hr))
        )
      }

      if (!matches.length) return null

      const firstName = (this.payload.first_name || '').trim().toLowerCase()
      const lastName = (this.payload.last_name || '').trim().toLowerCase()

      const exposedCvs = matches.filter(f => {
        const fileName = f.name.toLowerCase()
        return (firstName && fileName.includes(firstName)) || (lastName && fileName.includes(lastName))
      })

      const finalMatches = exposedCvs.length > 0 ? exposedCvs : matches
      finalMatches.sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at))

      return finalMatches[0]
    },
    toggle () {
      this.show_pdf = !this.show_pdf
    },
    applyView (view) {
      this.pdf_src = view.src
      this.pdfTitle = view.title
      this.pdfDate = view.date
    },
    async toggleOriginal () {
      if (this.is_loading_original) return

      if (this.showing_original) {
        if (this.processed_view) {
          this.applyView(this.processed_view)
        }
        this.showing_original = false
        return
      }

      if (this.original_view) {
        this.applyView(this.original_view)
        this.showing_original = true
        return
      }

      this.is_loading_original = true
      try {
        const files = await this.$network_utils.getFiles({
          resource_id: this.payload.resource_id,
          show_archive: true
        })
        const original = this.pickCv(files)

        if (!original) {
          this.$utils.emit('show_toast', { text: 'לא נמצא קובץ מקור' })
          return
        }

        const blobData = await this.$network_utils.getFile({
          file_name: original.name,
          resource_id: this.payload.resource_id,
          resource_type: 'Applicant',
          show_archive: true
        })

        const blob = new Blob([blobData], { type: 'application/pdf' })
        this.original_view = {
          src: URL.createObjectURL(blob) + '#toolbar=0&navpanes=0&zoom=100',
          title: original.name,
          date: original.uploaded_at
        }
        this.applyView(this.original_view)
        this.showing_original = true
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load original CV:', err)
        this.$utils.emit('show_toast', { text: 'לא נמצא קובץ מקור' })
      } finally {
        this.is_loading_original = false
      }
    }
  },
  components: {
    ApplicantProfilePdf
  }
}
</script>
<style>
  .profile-container {
    height: 90% !important;
  }
</style>
